import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, Loader2, MailCheck, MailOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useGetNotificationsByExpeditionIdBo,
  useToggleNotificationReadBo,
} from "@/api/notifications.api";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { Modal } from "@/components/ui/customs/modal";
import { LogDetail } from "@/components/backOffice/logs/logDetail";
import {
  NotificationItem,
  NotificationItemNotification,
  goToNotificationTarget,
} from "@/components/shared/notificationItem";
import type { NotificationByExpeditionIdDto } from "@/types/api/api-types";

type Props = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
};

export const NotificationModal = ({ open, onClose, expeditionId }: Props) => {
  const { t } = useTranslation("shared");
  const { data, isLoading, isError } = useGetNotificationsByExpeditionIdBo(expeditionId, {
    enabled: open,
  });

  const notifications = useMemo(() => {
    const list = data?.notifications ?? [];
    return [...list].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [data?.notifications]);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedNotification = useMemo(
    () => notifications.find((n) => n.id === selectedId) ?? notifications[0] ?? null,
    [notifications, selectedId],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("notificationModal.title")}
      size="lg"
      contentClassName="bg-[#F8FAFC] max-md:inset-0 max-md:translate-x-0 max-md:translate-y-0 max-md:w-screen max-md:max-w-none max-md:h-dvh max-md:max-h-none max-md:rounded-none max-md:content-start"
      dismissible
    >
      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="size-6 animate-spin text-gray-500" />
        </div>
      ) : isError ? (
        <div className="flex h-full items-center justify-center text-sm text-red-600">
          {t("notificationModal.loadError")}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          {t("notificationModal.empty")}
        </div>
      ) : (
        <div className="flex h-[calc(100dvh-8rem)] flex-col gap-6 overflow-hidden md:h-[60vh] md:flex-row md:gap-4">
          <div className="h-[30%] shrink-0 overflow-y-auto rounded-lg border-b border-gray-100 bg-white p-2 md:h-full md:max-w-70 md:border-r md:border-b-0 lg:max-w-100">
            {notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={toNotificationItemShape(n)}
                onSelect={() => setSelectedId(n.id)}
                isSelected={selectedNotification?.id === n.id}
              />
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg bg-white p-2 md:pl-6">
            {selectedNotification && (
              <NotificationDetail notification={toNotificationItemShape(selectedNotification)} />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

function toNotificationItemShape(n: NotificationByExpeditionIdDto): NotificationItemNotification {
  return {
    id: n.id,
    createdAt: n.createdAt,
    log: n.log
      ? {
          action: n.log.action,
          details: n.log.details,
          createdAt: n.log.createdAt,
          user: n.log.user ?? null,
        }
      : null,
    expedition: n.expedition
      ? {
          quotation: n.expedition.quotation ?? null,
          customQuotation: n.expedition.customQuotation ?? null,
        }
      : null,
    readAt: n.readAt ?? null,
    readBy: n.readBy ?? null,
  };
}

const NotificationDetail = ({
  notification,
  hideActions,
}: {
  notification: NotificationItemNotification;
  hideActions?: boolean;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("shared");
  const { mutate: toggleRead, isPending: isTogglingRead } = useToggleNotificationReadBo();

  const isUnread =
    notification.readById === null ||
    (notification.readById === undefined && notification.readAt == null);

  const log = notification.log;

  const handleNavigate = () => {
    if (!notification.expedition) return;
    goToNotificationTarget(notification.expedition, navigate);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!isUnread && notification.readBy && (
          <p className="mr-auto text-xs text-gray-500">
            {notification.readAt
              ? t("notificationDetail.readByOn", {
                  firstName: notification.readBy.firstName,
                  lastName: notification.readBy.lastName,
                  date: format(new Date(notification.readAt), "dd/MM/yyyy à HH:mm"),
                })
              : t("notificationDetail.readBy", {
                  firstName: notification.readBy.firstName,
                  lastName: notification.readBy.lastName,
                })}
          </p>
        )}
        {!hideActions && (
          <>
            {notification.expedition && (
              <DialogClose asChild>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-7 w-7"
                  title={t("notificationDetail.viewExpedition")}
                  onClick={handleNavigate}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </DialogClose>
            )}
            <Button
              type="button"
              size="icon"
              variant="outline"
              className={`h-7 w-7 ${isUnread ? "border-green-300 text-green-600 hover:border-green-400 hover:bg-green-50 hover:text-green-700" : "border-gray-200 text-gray-400 hover:border-gray-300 hover:bg-gray-50"}`}
              title={
                isUnread ? t("notificationDetail.markAsRead") : t("notificationDetail.markAsUnread")
              }
              disabled={isTogglingRead}
              onClick={() => toggleRead({ id: notification.id, read: isUnread })}
            >
              {isTogglingRead ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : isUnread ? (
                <MailCheck className="h-3.5 w-3.5" />
              ) : (
                <MailOpen className="h-3.5 w-3.5" />
              )}
            </Button>
          </>
        )}
      </div>

      {log && (
        <LogDetail
          log={{
            action: log.action ?? "",
            entityType: "",
            createdAt: log.createdAt ?? "",
            user: log.user
              ? { firstName: log.user.firstName ?? "", lastName: log.user.lastName ?? "" }
              : null,
            details: log.details,
          }}
        />
      )}
    </div>
  );
};

type NotificationDetailModalProps = {
  notification: NotificationItemNotification | null;
  open: boolean;
  onClose: () => void;
  hideActions?: boolean;
};

export const NotificationDetailModal = ({
  notification,
  open,
  onClose,
  hideActions,
}: NotificationDetailModalProps) => {
  const { t } = useTranslation("shared");
  if (!notification) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("notificationDetail.title")}
      size="lg"
      dismissible
    >
      <NotificationDetail notification={notification} hideActions={hideActions} />
    </Modal>
  );
};
